<template>
    <div id="exportable-content">
        <Hero title="Clear your integration backlog without growing engineering"
            :highlight-words="['integration', 'backlog']"
            :subtitle="`Say yes to the integrations your customers and sales team ask for. Your engineers build once per category, and Unified delivers ${INTEGRATIONS_COUNT_10s}+ integrations across ${CATEGORY_COUNT} categories, from CRM and ATS to HRIS and accounting.`"
            :footer-items="TRIAL_POINTS" :buttons="[
                {
                    link: 'https://app.unified.to',
                    title: 'Start free 30-day trial',
                },
                {
                    link: 'https://calendly.com/d/cph9-g8n-jzg/connect-with-unified',
                    title: 'Book a demo',
                    icon: 'ic:round-keyboard-arrow-right',
                },
            ]" />
    </div>

    <div class="py-20">
        <Customers />
    </div>

    <MarginImageComponent title="Integration requests don't have to compete with your roadmap"
        image_url="/images/Data-for-AI.svg"
        subtitle="Integrate once per category, not one integration at a time, so you can:" :data="[
            'Launch integrations in days, not quarters. Support entire categories like CRM, ATS, HRIS and accounting without additional engineering work.',
            'Unblock deals and expansions. Support the integrations enterprise customers expect without delaying the sale.',
            'Scale without changing your product economics. Plans are priced by API volume, and every plan includes unlimited integrations.',
            'Keep engineering on your core product. Unified manages auth, retries and API changes, so your team never maintains connectors.',
            'Let your team prove it in the free trial: connect real accounts and make live API calls before you commit.',
        ]" :button="{ link: 'https://app.unified.to', title: 'Start free 30-day trial' }" />

    <CoverageCheck />

    <Benefits center title="Make your integrations product-ready" :benefits="[
        {
            title: 'Fresh data your product can rely on',
            body: 'Every call retrieves data directly from the source API when your product needs it. Your product and AI features reflect the current state of your customer’s data.',
        },
        {
            title: 'Bi-directional data',
            body: 'Updating a candidate, posting a note or closing a deal is standardized. Unified supports read and write actions across integrations, so your product can retrieve and update data without per-provider logic.',
        },
        {
            title: 'Broad vendor coverage',
            body: `Unified supports ${INTEGRATIONS_COUNT_10s}+ integrations across ${CATEGORY_COUNT} categories. You integrate once per category, then enable new integrations without additional engineering work.`,
        },
        {
            title: 'Deep use-case coverage',
            body: `Unified supports hundreds of use cases across ${CATEGORY_COUNT} categories with our detailed rich unified data-models. Plus with optional original data and our passthrough API, you will never be blocked.`,
        },
        {
            title: 'Simple account authorization',
            body: 'Unified handles API tokens, OAuth, token refresh and permission scopes for every connected integration. Your customers complete a single authorization flow, and your product immediately gains access to the data and actions it needs. You control which objects and actions are available to your product, so there are no surprises.',
        },
        {
            title: 'The most secure way to integrate',
            body: 'Your customer\'s data is never stored on our servers. Unified retrieves it directly from the source and returns it immediately — without caching or replication. Every connection is explicitly authorized by the customer and scoped to exactly what your product needs.',
        },
    ]" />

    <Benefits center title="Ready for your security review"
        subtitle="Everything your security, legal and procurement teams will ask for, in one place."
        :benefits="[
            {
                title: 'Compliance',
                body: 'SOC 2 Type II, GDPR, CCPA/CPRA, HIPAA and PIPEDA compliant. Details are in our <a href=&quot;https://app.mycroft.io/trust/unified-to&quot; target=&quot;_blank&quot; class=&quot;text-blue-500 underline&quot;>Trust Center</a>.',
            },
            {
                title: 'Zero data storage',
                body: 'End-customer data is never stored or cached. Traffic is stateless, which removes data-at-rest risk and reduces your compliance scope.',
            },
            {
                title: 'Data residency',
                body: 'Run in the US, EU or Australian region to keep API traffic where your customers need it.',
            },
            {
                title: 'Scoped, customer-approved access',
                body: 'Every connection is authorized by your customer and limited to the objects and actions you request.',
            },
            {
                title: 'Contracts and subprocessors',
                body: 'Review our <a href=&quot;/gdpr/dpa&quot; class=&quot;text-blue-500 underline&quot;>Data Processing Agreement</a> and <a href=&quot;/gdpr/subprocessors&quot; class=&quot;text-blue-500 underline&quot;>subprocessor list</a> before you sign.',
            },
            {
                title: 'Security overview',
                body: 'Our security architecture and practices on one page you can send to your security team.',
                button_text: 'Read the security overview',
                button_link: '/security',
            },
        ]" />

    <BuildVsBuyCalculator />

    <Benefits center title="Pricing that follows usage, not headcount"
        subtitle="Plans are priced by API volume, and every plan includes unlimited integrations."
        button_text="Estimate your plan" button_link="/pricing#calculator" :benefits="[
            {
                title: 'Free 30-day trial',
                body: 'Start without a credit card. Connect real accounts, make live API calls and validate your use case before you pay anything.',
            },
            {
                title: 'Usage-based plans',
                body: 'Choose a plan by monthly API calls when you launch, then move up as your customers adopt your integrations.',
            },
            {
                title: 'No lock-in',
                body: 'Own your OAuth credentials and migrate any time. Monthly and yearly billing are both available.',
            },
        ]" />

    <Testimonials title="Why product teams choose Unified" :featured="['Oz L.', 'Kevin K.', 'Lucian S.']" />

    <Hero title="One Integration. All customer requirements met."
        :highlightWords="['All', 'customer', 'requirements', 'met.']"
        :subtitle="`Access your end-user's authorized SaaS data from ${INTEGRATIONS_COUNT_10s}+ integrations. Get started without adding headcount or shifting focus away from your core product.`"
        bg :footer-items="TRIAL_POINTS" :buttons="[{
            link: 'https://app.unified.to',
            title: 'Start free 30-day trial',
        },
        {
            link: '/contact',
            title: 'Speak to an expert',
            icon: 'ic:round-keyboard-arrow-right',
        }]" />
</template>


<script setup lang="ts">
import { INTEGRATIONS_COUNT_10s } from '~/services/integrations';
import { MAP_CATS } from '~/services/categories';

const CATEGORY_COUNT = Object.keys(MAP_CATS).length;

// Prospects often miss that the trial needs no card and works with real accounts.
const TRIAL_POINTS = ['Free for 30 days', 'No credit card required', 'Test with real customer accounts'];

useSEO({
    title: 'Unified for Product Managers',
    description: `Clear your integration backlog without growing engineering. Build once per category and launch ${INTEGRATIONS_COUNT_10s}+ integrations across ${CATEGORY_COUNT} categories. Free 30-day trial, no credit card required.`,
});

</script>
